import { TypeStringDto } from 'app/api/apiClient/generated/types';
import { BankAccountTypeId } from 'app/constants/bank-accounts';

import { useGetCanadianProvinceList } from './states/useGetCanadianProvinceList';
import { useGetUnitedStatesList } from './states/useGetUnitedStatesList';

export enum Country {
  Canada = 'CA',
  Mexico = 'MX',
  UnitedStates = 'US',
}

export enum StateLabel {
  Province,
  State,
}

export interface CountryDetails {
  iso: Country;
  stateLabel: StateLabel;
  stateList: TypeStringDto[];
}

// note: this should likely be controlled by the server
// future updates should consider adding an endpoint to retrieve supported country details
export const supportedCountries = [
  Country.Canada,
  Country.Mexico,
  Country.UnitedStates,
];

export const useGetCountryDetails = (): Record<Country, CountryDetails> => {
  const canadaProvinceList = useGetCanadianProvinceList();
  const mexicoStateList: TypeStringDto[] = []; // Mexico states can be added here in the future
  const unitedStatesList = useGetUnitedStatesList();

  const countryDetails: Record<Country, CountryDetails> = {
    [Country.Canada]: {
      iso: Country.Canada,
      stateLabel: StateLabel.Province,
      stateList: canadaProvinceList,
    },
    [Country.Mexico]: {
      iso: Country.Mexico,
      stateLabel: StateLabel.State,
      stateList: mexicoStateList,
    },
    [Country.UnitedStates]: {
      iso: Country.UnitedStates,
      stateLabel: StateLabel.State,
      stateList: unitedStatesList,
    },
  };

  return countryDetails;
};

export const useGetCountryDetailsByBankAccountType = (bankAccountTypeId: BankAccountTypeId): CountryDetails[] => {
  const allCountryDetails = useGetCountryDetails();

  const countryDetailsLookup: Partial<Record<BankAccountTypeId, CountryDetails[]>> = {
    [BankAccountTypeId.Ach]: [allCountryDetails[Country.UnitedStates]],
    [BankAccountTypeId.CadAch]: [allCountryDetails[Country.Canada], allCountryDetails[Country.UnitedStates]],
    [BankAccountTypeId.Check]: [allCountryDetails[Country.UnitedStates], allCountryDetails[Country.Canada]],
    [BankAccountTypeId.MxnAch]: [allCountryDetails[Country.Mexico]],
  };

  return countryDetailsLookup[bankAccountTypeId] ?? [];
};
